// Unpublished Work © 2023-2024 Deere & Company.

import { Alert, useToastProvider } from "@deere/fuel-react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, TextField, Tooltip, Typography } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  gridFilteredTopLevelRowCountSelector,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowsProp,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import { useOktaAuth } from "@okta/okta-react";

import { parse } from "papaparse";
import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import PricingApi from "../../../../../api/pricing/pricingApi";
import { PricingPayload } from "../../../../../api/pricing/pricingInterfaces";
import usePricingApi from "../../../../../api/pricing/usePricingApi";
import {
  LineValidationRequest,
  LineValidationResponse,
} from "../../../../../api/validation/validationInterfaces";
import xtremeBffApi from "../../../../../api/xtremeBff/xtremeBffApi";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import StickyFooter from "../../../../../components/StickyFooter/StickyFooter";
import { ConfigContext } from "../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { CATEGORIES, TOGGLES } from "../../../../featureToggles/constants";
import OrderLinesPagination from "../../../components/ReviewLinesPagination/OrderLinesPagination";
import { Line, LineState, Order, OrderType } from "../../../types";
import ContinueToNextWarningOrderModal from "./components/ContinueToNextWarningOrderModal/ContinueToNextWarningOrderModal";
import EntryLinesHeader from "./components/EntryLinesHeader/EntryLinesHeader";
import EntryLinesToolbar from "./components/EntryLinesToolbar/EntryLinesToolbar";
import OrderFileUploadModal from "./components/OrderFileUploadModal/OrderFileUploadModal";
import {
  CsvRowType,
  EntryLine,
  LineValidationState,
  OrderEntryRows,
} from "./types";
import {
  convertEntryLineToLine,
  createEntryLines,
  createEntryLinesFromLines,
  EntryLineBase,
  filterLinesWithSuccessfulValidation,
  getSerialNumberMaxLength,
  parseLineValidationResponse,
  sanitizeSerialNumberInput,
  ValidatedEntryLine,
} from "./utils";
import useXtremeBffApi from "../../../../../api/xtremeBff/useXtremeBff";

interface EntryLinesProps {
  order: Order;
  save: (lines: Line[]) => void;
  continueToNextButton: ReactNode;
  cancelOrderButton: ReactNode;
}

const DEFAULT_ENTRY_LINES_COUNT = 10;
const PAGE_SIZE = 50;

const EntryLines = forwardRef(
  (
    { order, save, continueToNextButton, cancelOrderButton }: EntryLinesProps,
    ref
  ) => {
    const theme = useTheme();
    const apiRef = useGridApiRef();
    const xtremeBffApiHook = useXtremeBffApi();

    const focusCellInput = (id: string, field: string) => {
      try {
        apiRef.current.setCellFocus(id, field);
        requestAnimationFrame(() => {
          const cellElement = (apiRef.current as any).getCellElement?.(
            id,
            field
          ) as HTMLElement | null;
          const inputElement = cellElement?.querySelector(
            ".MuiOutlinedInput-input"
          ) as HTMLInputElement | HTMLTextAreaElement | null;
          if (inputElement) {
            (inputElement as HTMLInputElement).focus();
            (inputElement as HTMLInputElement).select?.();
          }
        });
      } catch {
        console.log("Error while focusing the cell input");
      }
    };

    const getAdjacentField = (
      currentField: string,
      direction: 1 | -1
    ): { field: string | null } => {
      const columnNames = apiRef.current.state.columns.orderedFields.slice(1);
      const idx = columnNames.indexOf(currentField);
      if (idx === -1 || columnNames.length === 0) {
        return { field: columnNames[0] };
      }

      let nextIdx = idx + direction;
      if (nextIdx < 0) {
        nextIdx = columnNames.length - 1;
        return { field: columnNames[nextIdx] };
      }

      if (nextIdx >= columnNames.length) {
        nextIdx = 0;
        return { field: columnNames[nextIdx] };
      }

      return { field: columnNames[nextIdx] };
    };

    const { oktaAuth } = useOktaAuth();
    const {
      XTREME_BFF_SERVICE_URL,
      PRICING_SERVICE_URL,
      ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE,
      ENABLE_RBAC_AUTH_SUPPORT,
      ENABLE_RBAC_AUTH_SUPPORT_FOR_PRICING,
      ENABLE_RBAC_AUTH_SUPPORT_FOR_VALIDATIONS_SERVICE,
      ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
      ENABLE_MAX_ORDER_LINES_UI_VALIDATION,
      MAX_ORDER_LINES,
    } = useContext(ConfigContext);
    const { isToggleEnabled } = useContext(FeatureToggleContextV2);
    const { t } = useTranslation(["orders", "common"]);
    const { enqueueToast } = useToastProvider();
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const [viewIssuesOnly, setViewIssuesOnly] = useState(false);

    const changeViewIssuesOnly = (checked: boolean) => {
      setViewIssuesOnly(checked);
    };

    const isPromotionOrderToggleOn =
      ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE &&
      isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.ENABLE_PROMOTION_ORDER);

    const { dealerAccount } = useContext(DealerAccountContext);
    const isHazardousEnabled = useMemo(() => {
      if (ENABLE_FEATURE_TOGGLE_CONTEXT_V2) {
        return isToggleEnabled(
          CATEGORIES.PARTS,
          TOGGLES.ENABLE_HAZARDOUS_PARTS
        );
      }
      return false;
    }, [dealerAccount?.id]);

    const isSerialNumberEnabled = useMemo(() => {
      if (ENABLE_FEATURE_TOGGLE_CONTEXT_V2) {
        return isToggleEnabled(
          CATEGORIES.EXTREME_UI,
          TOGGLES.ENABLE_SERIAL_NUMBER
        );
      }
      return false;
    }, [dealerAccount?.id]);

    const promotionOrderFlowEnabled =
      isPromotionOrderToggleOn && order.orderType === OrderType.PROMOTION_ORDER;

    const getAccessToken = (): string => {
      const authToken = oktaAuth.getAccessToken();
      if (!authToken) return "";
      return authToken;
    };

    const MemoizedHeader = React.memo(({ title }: { title: string }) => (
      <Typography sx={columnsHeaderSx}>{title}</Typography>
    ));

    const MemoizedLinkButton = React.memo(({ url }: { url: string }) => (
      <Link
        to={url}
        style={{
          color: theme.tokens.FuelColorBrandPrimary,
        }}
        target="_blank"
        color={theme.tokens.FuelColorBorderLight}
        data-testid="partNumber-link"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
        }}
        tabIndex={-1}
      >
        <OpenInNewIcon
          fontSize="small"
          data-testid="openInNewIcon-enabled"
          sx={{
            position: "relative",
            left: theme.tokens.FuelSpacing1,
            margin: theme.tokens.FuelBorderRadiusLg,
          }}
        />
      </Link>
    ));

    const columns: GridColDef[] = [
      {
        field: "partNumber",
        renderHeader: () => (
          <MemoizedHeader
            title={t("orderLines.table.columnHeader.partNumber")}
          />
        ),
        renderCell: (params: GridRenderCellParams<EntryLine>) => {
          const url = `/dealer/${order.accountId}/parts/${encodeURIComponent(
            params.value
          )}`;
          return (
            <>
              <TextField
                id="partNumber-render"
                placeholder={t("input.placeholder") || "Enter input here..."}
                type="text"
                size="large"
                value={params.value}
                inputProps={{ maxLength: 30 }}
                helperText={t(params.row.partNumberWarningMessage)}
                error={
                  params.row.partNumberWarningMessage !== "" &&
                  !params.row.showPartNumberInfo
                }
              />
              {params.value &&
                (!params.row.showPartNumberWarning ||
                  params.row.showPartNumberInfo) && (
                  <MemoizedLinkButton url={url} />
                )}
            </>
          );
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
          <TextField
            id="partNumber-edit"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={params.value}
            inputProps={{ maxLength: 30 }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const newValue = event.target.value.replace(
                /[^a-zA-Z0-9\-/]/g,
                ""
              );
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue,
              });
              if (allLinesProcessed) {
                setAllLinesProcessed(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                const currentIndex = visibleRows.findIndex(
                  (r) => r.id === params.id
                );
                const currentField = params.field as string;
                if (event.shiftKey) {
                  if (currentIndex > 0) {
                    const previousRow = visibleRows[currentIndex - 1];
                    const { field } = getAdjacentField(currentField, -1);
                    focusCellInput(previousRow.id as string, field as string);
                  } else {
                    (event.target as HTMLElement).blur();
                  }
                } else {
                  const { field } = getAdjacentField(currentField, 1);
                  focusCellInput(params.id as string, field as string);
                }
              }
            }}
          />
        ),
        flex: 1,
        sortable: false,
        editable: true,
        headerAlign: "left",
        align: "left",
        minWidth: 120,
      },
      {
        field: "quantity",
        renderHeader: () => (
          <MemoizedHeader title={t("orderLines.table.columnHeader.quantity")} />
        ),
        renderCell: (params: GridRenderCellParams) => (
          <TextField
            id="quantity-render"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={params.value || ""}
            error={params.row.quantityWarningMessage !== ""}
            helperText={t(params.row.quantityWarningMessage)}
          />
        ),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <TextField
            id="quantity-edit"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={params.value || ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const inputValue = event.target.value;
              const isValidInput =
                inputValue === "" ||
                (!Number.isNaN(Number(inputValue)) &&
                  Number(inputValue) >= 1 &&
                  Number(inputValue) <= 999999);

              if (isValidInput) {
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: inputValue === "" ? "" : Number(inputValue),
                });
              }
              if (allLinesProcessed) {
                setAllLinesProcessed(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                const tabDirection = event.shiftKey ? -1 : 1;
                const currentField = params.field as string;
                const { field } = getAdjacentField(currentField, tabDirection);

                focusCellInput(params.id as string, field as string);
              }
            }}
          />
        ),
        flex: 0.6,
        sortable: false,
        editable: true,
        headerAlign: "left",
        align: "left",
        minWidth: 100,
      },
      {
        field: "bin",
        renderHeader: () => (
          <MemoizedHeader title={t("orderLines.table.columnHeader.bin")} />
        ),
        renderCell: (params: GridRenderCellParams) => (
          <TextField
            id="bin-render"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={params.value}
          />
        ),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <TextField
            id="bin-edit"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={params.value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value.length <= 12)
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: event.target.value,
                });
              if (allLinesProcessed) {
                setAllLinesProcessed(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                const currentIndex = visibleRows.findIndex(
                  (r) => r.id === params.id
                );

                if (event.shiftKey) {
                  const preField = getAdjacentField(params.field as string, -1);
                  focusCellInput(params.id as string, preField.field as string);
                } else {
                  const next =
                    currentIndex >= 0
                      ? visibleRows[currentIndex + 1]
                      : undefined;
                  const nextField = getAdjacentField(params.field as string, 1);
                  if (next) {
                    focusCellInput(
                      next.id as string,
                      nextField.field as string
                    );
                  } else {
                    (event.target as HTMLElement).blur();
                  }
                }
              }
            }}
          />
        ),
        flex: 1,
        sortable: false,
        editable: true,
        headerAlign: "left",
        align: "left",
      },
    ];

    // Serial Number column - only shown when required
    const serialNumberColumn: GridColDef = {
      field: "engineSerialNumber",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <MemoizedHeader
            title={t("orderLines.table.columnHeader.serialNumber")}
          />
          <Tooltip
            title={t("orderLines.serialNumber.columnHeader.tooltip")}
            placement="top"
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: "16px",
                color: theme.tokens.FuelColorTextTertiary,
                cursor: "help",
              }}
            />
          </Tooltip>
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<EntryLine>) => {
        if (!params.row.showSerialNumberField) {
          return null;
        }
        // Display array as comma-separated string
        const displayValue = Array.isArray(params.value)
          ? params.value.join(", ")
          : params.value || "";
        return (
          <TextField
            id="serialNumber-render"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={displayValue}
            error={params.row.serialNumberWarningMessage !== ""}
            helperText={t(params.row.serialNumberWarningMessage)}
          />
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => {
        const row = rows.find((r) => r.id === params.id);
        if (!row?.showSerialNumberField) {
          return null;
        }
        const quantity = row.quantity || 1;
        const maxLen = getSerialNumberMaxLength(quantity);
        // Display array as comma-separated string for editing, or keep raw string during typing
        const displayValue = Array.isArray(params.value)
          ? params.value.join(", ")
          : params.value || "";
        return (
          <TextField
            id="serialNumber-edit"
            placeholder={t("input.placeholder") || "Enter input here..."}
            type="text"
            size="large"
            value={displayValue}
            inputProps={{ maxLength: maxLen }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeSerialNumberInput(
                event.target.value,
                quantity
              );
              const serialNumberArray = sanitized
                ? sanitized.split(",").map((sn: string) => sn.trim())
                : [];
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: serialNumberArray,
              });
              if (allLinesProcessed) {
                setAllLinesProcessed(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                const currentIndex = visibleRows.findIndex(
                  (r) => r.id === params.id
                );

                if (event.shiftKey) {
                  const preField = getAdjacentField(params.field as string, -1);
                  focusCellInput(params.id as string, preField.field as string);
                } else {
                  const next =
                    currentIndex >= 0
                      ? visibleRows[currentIndex + 1]
                      : undefined;
                  const nextField = getAdjacentField(params.field as string, 1);
                  if (next) {
                    focusCellInput(
                      next.id as string,
                      nextField.field as string
                    );
                  } else {
                    (event.target as HTMLElement).blur();
                  }
                }
              }
            }}
          />
        );
      },
      flex: 1,
      sortable: false,
      editable: true,
      headerAlign: "left",
      align: "left",
      minWidth: 150,
    };

    const lines =
      order?.lines?.length === 0
        ? createEntryLines(DEFAULT_ENTRY_LINES_COUNT)
        : createEntryLinesFromLines(order.lines);

    const processEntryLines = (updatedLines: EntryLine[]): OrderEntryRows[] =>
      updatedLines
        .filter((update: EntryLine) => update.state === "SUBMITTED")
        .map((line: EntryLine) => ({
          partNumber: line.partNumber,
          quantity: line.quantity,
          bin: line.bin,
          uniqueLineId: line.uniqueLineId,
          engineSerialNumber: line.engineSerialNumber,
        }));

    const [uploadedRows, setUploadedRows] = useState<OrderEntryRows[]>(
      processEntryLines(lines)
    );
    const [selectedRow, setSelectedRow] = useState<GridRowsProp[]>([]);
    const [showContinueToNextWarningModal, setShowContinueToNextWarningModal] =
      useState<boolean>(false);
    const [showFileUploadModal, setShowFileUploadModal] =
      useState<boolean>(false);
    const [noValidLines, setNoValidLines] = useState<boolean>(false);
    const [hasMissingSerialNumbers, setHasMissingSerialNumbers] =
      useState<boolean>(false);
    const [processingOrder, setProcessingOrder] = useState<boolean>(false);
    const [allLinesProcessed, setAllLinesProcessed] = useState<boolean>(false);
    const [loadingIndicator, setLoadingIndicator] = useState<boolean>(false);
    const [isResetButtonDisabled, setIsResetButtonDisabled] =
      useState<boolean>(true);
    const [page, setPage] = useState<number>(0);
    const [rows, setRows] = useState<EntryLine[]>(lines);
    const [uniqueRows, setUniqueRows] = useState<number>(rows.length);
    const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
    const [isOrderLinesImporting, setIsOrderLinesImporting] =
      useState<boolean>(false);
    useEffect(() => {}, [loadingIndicator]);

    // Check if any row requires serial number field
    const hasSerialNumberRows = useMemo(
      () => rows.some((row) => row.showSerialNumberField),
      [rows]
    );

    // Dynamically include serial number column if needed
    const displayColumns = useMemo(() => {
      if (hasSerialNumberRows) {
        return [...columns, serialNumberColumn];
      }
      return columns;
    }, [hasSerialNumberRows, columns]);

    const enteredRowCount = () => rows.filter((row) => !row.action).length;

    const isPricingApiRbacAuthEnabled =
      ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PRICING;
    const pricingApiHook = usePricingApi();
    const isValidationServiceApiAuthEnable =
      ENABLE_RBAC_AUTH_SUPPORT &&
      ENABLE_RBAC_AUTH_SUPPORT_FOR_VALIDATIONS_SERVICE;
    const getPricingsFn = isPricingApiRbacAuthEnabled
      ? pricingApiHook.getPricings
      : (payload: PricingPayload) =>
          PricingApi.getPricings(
            PRICING_SERVICE_URL,
            payload,
            getAccessToken()
          );

    useEffect(() => {
      const unValidatedRows = rows.filter(
        (row) => (row.partNumber || row.quantity || row.bin) && !row.isValidated
      );
      if (!rows.find((row) => !row.action)) {
        setIsResetButtonDisabled(true);
      } else {
        setIsResetButtonDisabled(false);
      }
      setAllLinesProcessed(unValidatedRows.length === 0);
      if (rows.length < page * pageSize + 1) {
        setPage(0);
      }
    }, [rows]);

    useEffect(() => {
      async function processUploadedRows() {
        try {
          setLoadingIndicator(false);
          const processedLine = await processLines(uploadedRows);
          setRows((prevRows) => {
            const existingLines = prevRows.filter(
              (prevRow) => prevRow.partNumber || prevRow.quantity || prevRow.bin
            );
            // If after import the line count is less than the default entry lines count, add extra lines
            const extraLines = createEntryLines(
              DEFAULT_ENTRY_LINES_COUNT -
                (processedLine.length +
                  (isFileUploaded ? existingLines.length : 0))
            );
            if (uploadedRows.length > 0 && !isFileUploaded) {
              return [...processedLine, ...extraLines];
            }
            return [...processedLine, ...existingLines, ...extraLines];
          });
        } finally {
          setLoadingIndicator(false);
        }
      }

      if (uploadedRows.length > 0) {
        processUploadedRows().then();
      }
    }, [uploadedRows]);

    useEffect(() => {
      if (allLinesProcessed && processingOrder) {
        setLoadingIndicator(false);
        handleContinueToNext(true);
      }
    }, [allLinesProcessed, processingOrder]);

    useImperativeHandle(ref, () => ({
      propagateDelta: () => {
        setProcessingOrder(true);
        setLoadingIndicator(!(enteredRowCount() === numberOfErrors));
      },
      isEdited: () => rows.filter((row) => row.isEdited).length !== 0,
      clearEntries: () => {
        setRows(createEntryLines(DEFAULT_ENTRY_LINES_COUNT));
      },
    }));

    // ---------------- Line added hook - Start ------
    const lineValidation = (
      partNumber: string,
      quantity: number,
      countryCode: string,
      token: string,
      engineSerialNumber?: string[]
    ): Promise<LineValidationState> => {
      // Convert array to comma-separated string for API
      const engineSerialNumberString =
        engineSerialNumber && engineSerialNumber.length > 0
          ? engineSerialNumber.join(",")
          : undefined;
      const payload: LineValidationRequest = {
        accountId: order.accountId,
        partNumber,
        requestedQuantity: `${quantity}`,
        countryCode,
        ...(promotionOrderFlowEnabled
          ? {
              promotionId: order!.promotion!.id,
            }
          : {}),
        ...(engineSerialNumberString
          ? { engineSerialNumber: engineSerialNumberString }
          : {}),
      };

      return (
        isValidationServiceApiAuthEnable
          ? xtremeBffApiHook.lineValidation(XTREME_BFF_SERVICE_URL, payload)
          : xtremeBffApi.lineValidation(XTREME_BFF_SERVICE_URL, payload, token)
      )
        .then((response) =>
          parseLineValidationResponse(
            response,
            isHazardousEnabled,
            isSerialNumberEnabled
          )
        )
        .catch(() => ({
          isQuantityValid: true,
          quantityFailureMessage: "",
          isPartNumberValid: true,
          partNumberFailureMessage: "",
          isValidated: false,
          showPartNumberInfo: false,
          requiresSerialNumber: false,
          isSerialNumberValid: true,
          serialNumberFailureMessage: "",
        }));
    };

    const processLine = ({
      partNumber,
      quantity,
      bin,
      engineSerialNumber,
    }: EntryLineBase): Promise<ValidatedEntryLine> =>
      // console.log("processLine input:", { partNumber, quantity, bin, engineSerialNumber });
      lineValidation(
        partNumber,
        quantity,
        order.dealerLocation,
        getAccessToken()!,
        engineSerialNumber
      ).then((response) => ({
        partNumber,
        quantity,
        bin,
        action: false,
        isNew: false,
        isEdited: true,
        state: LineState.VALIDATED,
        showQuantityWarning: !response.isQuantityValid,
        quantityWarningMessage: response.quantityFailureMessage,
        showPartNumberWarning: !response.isPartNumberValid,
        partNumberWarningMessage: response.partNumberFailureMessage,
        showPartNumberInfo: response.showPartNumberInfo,
        isValidated: true,
        engineSerialNumber: engineSerialNumber || [],
        showSerialNumberField:
          (response.isPartNumberValid || response.showPartNumberInfo) &&
          response.requiresSerialNumber,
        showSerialNumberWarning:
          response.requiresSerialNumber && !response.isSerialNumberValid,
        serialNumberWarningMessage: response.serialNumberFailureMessage,
      }));
    const linesValidation = async (
      importLines: EntryLineBase[],
      countryCode: string,
      token: string
    ): Promise<EntryLine[]> => {
      const payload: LineValidationRequest[] = importLines.map((line) => {
        // Convert array to comma-separated string for API
        const engineSerialNumberString =
          line.engineSerialNumber && line.engineSerialNumber.length > 0
            ? line.engineSerialNumber.join(",")
            : undefined;
        return {
          accountId: order.accountId,
          partNumber: line.partNumber,
          requestedQuantity: `${line.quantity}`,
          countryCode,
          ...(promotionOrderFlowEnabled
            ? {
                promotionId: order!.promotion!.id,
              }
            : {}),
          ...(engineSerialNumberString
            ? { engineSerialNumber: engineSerialNumberString }
            : {}),
        };
      });

      try {
        setIsOrderLinesImporting(true);
        const responses = isValidationServiceApiAuthEnable
          ? await xtremeBffApiHook.linesValidation(
              XTREME_BFF_SERVICE_URL,
              payload
            )
          : await xtremeBffApi.linesValidation(
              XTREME_BFF_SERVICE_URL,
              payload,
              token,
              false
            );
        setIsOrderLinesImporting(false);
        return importLines.map((importLine) => {
          const lineResponse: LineValidationResponse =
            responses[`${importLine.partNumber}-${importLine.quantity}`];
          const lineValidationState: LineValidationState =
            parseLineValidationResponse(
              lineResponse,
              isHazardousEnabled,
              isSerialNumberEnabled
            );
          return {
            id: crypto.randomUUID(),
            quantity: importLine.quantity,
            showQuantityWarning: !lineValidationState.isQuantityValid,
            quantityWarningMessage: lineValidationState.quantityFailureMessage,
            partNumber: importLine.partNumber,
            showPartNumberWarning: !lineValidationState.isPartNumberValid,
            partNumberWarningMessage:
              lineValidationState.partNumberFailureMessage,
            showPartNumberInfo: lineValidationState.showPartNumberInfo,
            state: LineState.VALIDATED,
            bin: importLine.bin,
            action: false,
            isEdited: true,
            isNew: false,
            isValidated: true,
            uniqueLineId: importLine.uniqueLineId,
            engineSerialNumber: importLine.engineSerialNumber || [],
            showSerialNumberField:
              (lineValidationState.isPartNumberValid ||
                lineValidationState.showPartNumberInfo) &&
              lineValidationState.requiresSerialNumber,
            showSerialNumberWarning:
              lineValidationState.requiresSerialNumber &&
              !lineValidationState.isSerialNumberValid,
            serialNumberWarningMessage:
              lineValidationState.serialNumberFailureMessage,
          };
        });
      } catch {
        setIsOrderLinesImporting(false);
        return importLines.map((importLine) => ({
          id: crypto.randomUUID(),
          quantity: importLine.quantity,
          showQuantityWarning: false,
          quantityWarningMessage: "",
          partNumber: importLine.partNumber,
          showPartNumberWarning: false,
          partNumberWarningMessage: "",
          state: LineState.VALIDATED,
          showPartNumberInfo: false,
          bin: importLine.bin,
          action: false,
          isEdited: true,
          isNew: false,
          isValidated: true,
          engineSerialNumber: importLine.engineSerialNumber || [],
          showSerialNumberField: false,
          showSerialNumberWarning: false,
          serialNumberWarningMessage: "",
        }));
      }
    };

    const processLines = async (
      entryLines: EntryLineBase[]
    ): Promise<EntryLine[]> => {
      hitPricingService(
        order.accountId,
        entryLines.map((line) => line.partNumber)
      );
      return linesValidation(
        entryLines,
        order.dealerLocation,
        getAccessToken()!
      );
    };

    const hitPricingService = (accountId: string, partNumbers: string[]) => {
      const payload: PricingPayload = {
        accountId,
        partNumbers,
      };
      getPricingsFn(payload);
    };

    const updateRow = (
      newRow: EntryLineBase & {
        id: string;
      },
      updatedRow: EntryLine
    ) => {
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === newRow.id ? updatedRow : row))
      );
    };

    const processRowUpdate = async (newRow: EntryLineBase & { id: string }) => {
      let editedRow = newRow;
      // @ts-ignore
      if (editedRow.quantity === "") {
        editedRow = { ...editedRow, quantity: 0 };
      }

      const existingRow = rows.find((row) => row.id === editedRow.id);
      if (
        existingRow &&
        editedRow.partNumber === existingRow.partNumber &&
        editedRow.quantity === existingRow.quantity &&
        editedRow.bin === existingRow.bin &&
        JSON.stringify(editedRow.engineSerialNumber) ===
          JSON.stringify(existingRow.engineSerialNumber)
      ) {
        return existingRow;
      }

      if (
        editedRow.partNumber === "" &&
        editedRow.quantity === 0 &&
        editedRow.bin !== ""
      ) {
        const updatedRow: EntryLine = {
          ...editedRow,
          action: false,
          isEdited: true,
          isNew: false,
          state: LineState.VALIDATED,
          showQuantityWarning: true,
          quantityWarningMessage:
            "orderLines.quantity.quantityvalidation.failure",
          showPartNumberWarning: true,
          showPartNumberInfo: false,
          partNumberWarningMessage:
            "orderLines.partNumber.partnumbervalidation.failure",
          isValidated: true,
          engineSerialNumber: editedRow.engineSerialNumber || [],
          showSerialNumberField: false,
          showSerialNumberWarning: false,
          serialNumberWarningMessage: "",
        };
        updateRow(editedRow, updatedRow);
        return updatedRow;
      }

      if (
        existingRow &&
        editedRow.partNumber === existingRow.partNumber &&
        editedRow.quantity === existingRow.quantity &&
        editedRow.bin !== existingRow.bin &&
        JSON.stringify(editedRow.engineSerialNumber) ===
          JSON.stringify(existingRow.engineSerialNumber)
      ) {
        updateRow(editedRow, { ...existingRow, bin: editedRow.bin });
        return { ...existingRow, bin: editedRow.bin };
      }

      if (
        existingRow &&
        editedRow.partNumber === existingRow.partNumber &&
        editedRow.quantity === existingRow.quantity &&
        editedRow.bin === existingRow.bin &&
        JSON.stringify(editedRow.engineSerialNumber) !==
          JSON.stringify(existingRow.engineSerialNumber)
      ) {
        // Filter out empty strings when saving
        const engineSerialNumberArray = (
          editedRow.engineSerialNumber || []
        ).filter((sn: string) => sn !== "");
        const validEntryLine = await processLine({
          ...editedRow,
          engineSerialNumber: engineSerialNumberArray,
        });
        const updatedSerialNumberRow = {
          ...validEntryLine,
          id: editedRow.id,
          engineSerialNumber: engineSerialNumberArray,
        };
        updateRow(editedRow, updatedSerialNumberRow);
        return updatedSerialNumberRow;
      }

      if (editedRow.partNumber === "" && editedRow.quantity > 0) {
        const updatedRow: EntryLine = {
          ...editedRow,
          action: false,
          isEdited: true,
          isNew: false,
          state: LineState.VALIDATED,
          showQuantityWarning: false,
          quantityWarningMessage: "",
          showPartNumberWarning: true,
          showPartNumberInfo: false,
          partNumberWarningMessage:
            "orderLines.partNumber.partnumbervalidation.failure",
          isValidated: true,
          engineSerialNumber: editedRow.engineSerialNumber || [],
          showSerialNumberField: false,
          showSerialNumberWarning: false,
          serialNumberWarningMessage: "",
        };
        updateRow(editedRow, updatedRow);
        return updatedRow;
      }

      let updatedRow: EntryLine;

      if (editedRow.partNumber || editedRow.quantity || editedRow.bin) {
        const validEntryLine = await processLine(editedRow);
        updatedRow = { ...validEntryLine, id: editedRow.id };
      } else {
        updatedRow = {
          ...editedRow,
          action: true,
          isEdited: false,
          isNew: false,
          state: LineState.VALIDATED,
          showQuantityWarning: false,
          quantityWarningMessage: "",
          showPartNumberWarning: false,
          partNumberWarningMessage: "",
          showPartNumberInfo: false,
          isValidated: false,
          engineSerialNumber: [],
          showSerialNumberField: false,
          showSerialNumberWarning: false,
          serialNumberWarningMessage: "",
        };
      }
      updateRow(editedRow, updatedRow);
      return updatedRow;
    };

    const handleContinueToNext = (checkModalWarning: boolean) => {
      const validatedRows = filterLinesWithSuccessfulValidation(rows);
      // Check if current row count exceeds maximum
      if (
        ENABLE_MAX_ORDER_LINES_UI_VALIDATION &&
        enteredRowCount() > MAX_ORDER_LINES
      ) {
        setProcessingOrder(false);
        setLoadingIndicator(false);
        enqueueToast({
          variant: "error",
          message: t("orders:orderLines.error.maxLinesExceeded"),
        });
        return; // Block proceeding when exceeding max lines
      }

      if (checkModalWarning) {
        const allRowsInvalid = validatedRows.length === 0;
        const oneRowInvalid = rows.find(
          (row) =>
            (row.showPartNumberWarning && !row.showPartNumberInfo) ||
            row.showQuantityWarning ||
            row.showSerialNumberWarning
        );

        // Check if any validated row requires a serial number but doesn't have one
        const missingSerialNumbers = rows.some(
          (row) =>
            row.showSerialNumberField &&
            (!row.engineSerialNumber || row.engineSerialNumber.length === 0) &&
            row.isValidated &&
            !row.showPartNumberWarning &&
            !row.showQuantityWarning
        );

        if (missingSerialNumbers) {
          setShowContinueToNextWarningModal(true);
          setNoValidLines(true); // Block proceeding - serial numbers are required
          setHasMissingSerialNumbers(true);
          return;
        }

        if (oneRowInvalid || allRowsInvalid) {
          setShowContinueToNextWarningModal(true);
          setNoValidLines(allRowsInvalid);
          setHasMissingSerialNumbers(false);
          return;
        }
      }

      setProcessingOrder(false);

      const orderLines: Line[] = validatedRows
        .map(convertEntryLineToLine)
        .map((line, idx) => ({
          lineNumber: idx + 1,
          ...line,
        }));

      save(orderLines);
    };

    const onRowsSelectionHandler = (ids: any) => {
      const selectedRowsData = ids.map((id: string) =>
        rows.find((row) => row.id === id)
      );
      setSelectedRow(selectedRowsData);
    };

    const addRows = (count: number) => {
      const createdLines = createEntryLines(count);
      setRows((prevRows) => [...prevRows, ...createdLines]);

      setTimeout(() => {
        let lastPage = Math.ceil(rows.length / pageSize) - 1;
        const lastFilledRow = (rows.length + count) % pageSize === 0;
        if (lastFilledRow) lastPage += 1;
        setPage(lastPage);
        apiRef.current?.setPage(lastPage);
        apiRef.current?.setPaginationModel({
          pageSize,
          page: lastPage,
        });
        window.scroll(0, 1000 * rows.length);
      }, 100);
    };

    const deleteButtonCallback = () => {
      const listOfIds = selectedRow
        .filter(
          (individualRow: GridRowModel) =>
            individualRow.partNumber ||
            individualRow.quantity ||
            individualRow.bin
        )
        .map((row: GridRowModel) => row.id);

      const rowsCountAfterDelete = rows.length - listOfIds.length;

      setRows((prevRows) =>
        prevRows.filter((row) => !listOfIds?.includes(row.id))
      );
      if (rowsCountAfterDelete < DEFAULT_ENTRY_LINES_COUNT) {
        const rowsToBeAdded = DEFAULT_ENTRY_LINES_COUNT - rowsCountAfterDelete;
        setRows((prevRows) => [
          ...prevRows,
          ...createEntryLines(rowsToBeAdded),
        ]);
      }

      if (showRowsWithIssuesOnly) {
        const allRowsSelected = selectedRow.length === visibleRows.length;
        if (allRowsSelected) setShowRowsWithIssuesOnly(false);
      }
      setSelectedRow([]);
      enqueueToast({
        variant: "success",
        message: t("orders:orderLines.deleteRow.success.toast", {
          count: listOfIds.length,
        }),
        isCloseable: true,
      });
    };

    const resetButtonCallback = () => {
      setShowRowsWithIssuesOnly(false);
      setRows(createEntryLines(DEFAULT_ENTRY_LINES_COUNT));
      setPage(0);
    };

    const parseFiles = (files: File[]) => {
      if (files && files.length > 0) {
        const parsePromises: Promise<OrderEntryRows[]>[] = [];

        files.forEach((file) => {
          const fileType = file.name.split(".").slice(-1);
          if (file && fileType?.includes("csv")) {
            const parsePromise = new Promise<OrderEntryRows[]>((resolve) => {
              parse<CsvRowType>(file, {
                header: true,
                skipEmptyLines: true,
                complete(results) {
                  // trim column spaces over here
                  if (
                    results.meta.fields !== undefined &&
                    (results.meta.fields.length > 3 ||
                      !results.meta.fields?.includes("Quantity") ||
                      !results.meta.fields?.includes("PartNumber") ||
                      !results.meta.fields?.includes("Bin"))
                  ) {
                    enqueueToast({
                      variant: "error",
                      message: t(
                        "orders:orderLines.orderGrid.importLines.incorrectData"
                      ),
                      isCloseable: true,
                    });
                  } else {
                    const isNotEmpty = (value: string | undefined) =>
                      value && value.trim();

                    const isNumber = (value: string) =>
                      !Number.isNaN(Number(value.trim()));

                    const filterEmptyRows = results.data.filter(
                      (fileRows) =>
                        isNotEmpty(fileRows.PartNumber) ||
                        (isNotEmpty(fileRows.Quantity) &&
                          isNumber(fileRows.Quantity)) ||
                        isNotEmpty(fileRows.Bin)
                    );
                    const trimmedValue = (value: string | undefined) =>
                      value !== undefined ? value.trim() : "";

                    const filteredData = filterEmptyRows.map((row) => ({
                      partNumber: trimmedValue(row.PartNumber).toUpperCase(),
                      quantity: Number(trimmedValue(row.Quantity)),
                      bin: trimmedValue(row.Bin),
                    }));

                    const editedRowLength = rows.filter(
                      (row) => !row.action
                    ).length;
                    const sum = editedRowLength + filteredData.length;

                    if (filteredData.length > MAX_ORDER_LINES) {
                      enqueueToast({
                        variant: "error",
                        message: t(
                          "orders:orderLines.orderGrid.importLines.exceed.numberOfLines",
                          {
                            maxLines: MAX_ORDER_LINES,
                          }
                        ),
                        isCloseable: true,
                      });
                    } else if (sum > MAX_ORDER_LINES) {
                      enqueueToast({
                        variant: "error",
                        message: t(
                          "orders:orderLines.orderGrid.totalLines.exceed.numberOfLines",
                          {
                            maxLines: MAX_ORDER_LINES,
                            remainingLines: MAX_ORDER_LINES - editedRowLength,
                          }
                        ),
                        isCloseable: true,
                      });
                    } else if (
                      filteredData.length > 0 &&
                      filteredData.length <= MAX_ORDER_LINES &&
                      sum <= MAX_ORDER_LINES
                    ) {
                      setIsFileUploaded(true);
                      resolve(filteredData);
                    } else {
                      enqueueToast({
                        variant: "error",
                        message: t(
                          "orders:orderLines.orderGrid.importLines.empty",
                          {
                            maxLines: MAX_ORDER_LINES,
                          }
                        ),
                        isCloseable: true,
                      });
                    }
                  }
                },
              });
            });
            parsePromises.push(parsePromise);
          }
        });

        Promise.all(parsePromises).then((parsedData) => {
          setUploadedRows(parsedData.flat());
        });
      }
    };

    const columnsHeaderSx = useMemo(
      () => ({
        fontWeight: theme.tokens.FuelFontWeightSemibold,
        fontSize: theme.tokens.FuelFontSizeHeading6,
      }),
      [theme]
    );

    const dataGridSx = useMemo(
      () => ({
        border: 1,
        borderColor: theme.tokens.FuelColorBorderLight,
        [`.${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
          outline: "none",
        },
        [`.${gridClasses.cell}.${gridClasses["cell--editable"]}`]: {
          display: "flex",
          padding: "10px",
        },
        [`.${gridClasses.cell}.${gridClasses["cell--editing"]}`]: {
          boxShadow: "none !important",
          padding: "10px",
          outline: "none !important",
          backgroundColor: "none",
        },
        [`.${gridClasses.cell}.${gridClasses["cell--editing"]}:has(.Mui-focused)`]:
          {
            backgroundColor: `${theme.tokens.FuelColorBackgroundActionDefaultHovered}`,
          },
        [`.${gridClasses.row}.Mui-selected>.${gridClasses.cell}`]: {
          backgroundColor: `${theme.tokens.FuelColorBackgroundActionTertiaryHovered} !important`,
        },
        [`.${gridClasses.row}.Mui-selected:hover>.${gridClasses.cell}`]: {
          backgroundColor: `${theme.tokens.FuelColorBackgroundActionDefaultPressed} !important`,
        },
        [`& .${gridClasses.row}`]: {
          border: 1,
          borderLeft: 0,
          borderBottom: 0,
          borderColor: theme.tokens.FuelColorBorderLight,
        },
        [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
          backgroundColor: theme.tokens.FuelColorBackgroundWhite,
          borderColor: theme.tokens.FuelColorBorderLight,
        },
        [`& .${gridClasses.columnHeader}`]: {
          borderBottom: 0,
        },
        [`.${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
          {
            outline: "none",
          },
        [`.${gridClasses.columnSeparator}`]: {
          color: theme.tokens.FuelColorBorderLight,
        },
        [`.${gridClasses.virtualScroller}`]: {
          overflowY: "hidden",
        },
        [`.${gridClasses.checkboxInput}`]: {
          color: theme.tokens.FuelColorGray400,
        },
        [`.${gridClasses.cellCheckbox}>span`]: {
          marginTop: theme.tokens.FuelSpacing1,
          marginBottom: theme.tokens.FuelSpacing1,
        },
        [`.${gridClasses.cell}`]: {
          padding: "10px",
          alignItems: "flex-start",
        },
        [`.${gridClasses.filler}`]: {
          height: "0px !important",
        },
        [`.Mui-error`]: {
          borderColor: theme.tokens.FuelColorAlertErrorIcon,
        },
        "& .MuiDataGrid-row.Mui-selected .MuiSvgIcon-root": {
          color: theme.tokens.FuelColorGreen700,
        },
        "--unstable_DataGrid-radius": "8px",
      }),
      [theme, gridClasses]
    );

    const numberOfErrors = useMemo(
      () =>
        rows.filter(
          (row) =>
            (row.showPartNumberWarning && !row.showPartNumberInfo) ||
            row.showQuantityWarning ||
            row.showSerialNumberWarning
        ).length,
      [rows]
    );

    const [showRowsWithIssuesOnly, setShowRowsWithIssuesOnly] = useState(false);

    const visibleRows = useMemo(() => {
      if (!showRowsWithIssuesOnly) {
        return rows;
      }
      return rows.filter(
        (row) =>
          row.showPartNumberWarning ||
          row.showQuantityWarning ||
          row.showSerialNumberWarning
      );
    }, [rows, showRowsWithIssuesOnly]);

    const rightClickContextRef = React.useRef<{
      rowId?: GridRowId;
      field?: string;
      timestamp?: number;
    }>({});

    const handleCellClick = React.useCallback(
      (params: any, event: React.MouseEvent) => {
        if (event.button === 2) {
          rightClickContextRef.current = {
            rowId: params.id,
            field: params.field,
            timestamp: Date.now(),
          };
        }
      },
      []
    );

    const handlePasteEvent = React.useCallback(() => {
      if (!apiRef.current) return;

      let targetCell = rightClickContextRef.current;
      const now = Date.now();

      if (
        !targetCell.rowId ||
        !targetCell.timestamp ||
        now - targetCell.timestamp > 3000
      ) {
        const focusedCell = apiRef.current.state.focus.cell;
        if (focusedCell) {
          targetCell = {
            rowId: focusedCell.id,
            field: focusedCell.field,
          };
        }
      }

      if (targetCell.rowId && targetCell.field) {
        try {
          apiRef.current.startRowEditMode({ id: targetCell.rowId });
          setTimeout(() => {
            apiRef.current.setCellFocus(targetCell.rowId!, targetCell.field!);
          }, 0);
        } catch (error) {
          console.warn("Error starting row edit mode:", error);
        }
      }

      rightClickContextRef.current = {};
    }, [apiRef]);

    React.useEffect(() => {
      document.addEventListener("paste", handlePasteEvent);
      return () => document.removeEventListener("paste", handlePasteEvent);
    }, [handlePasteEvent]);

    const importLinesButtonCallback = () => {
      setShowFileUploadModal(true);
    };

    return (
      <>
        {loadingIndicator && <LoadingIndicator overlayLoader />}

        {!(enteredRowCount() === numberOfErrors) &&
          showContinueToNextWarningModal && (
            <ContinueToNextWarningOrderModal
              open={showContinueToNextWarningModal}
              closeModal={() => {
                setShowContinueToNextWarningModal(false);
                setNoValidLines(false);
                setHasMissingSerialNumbers(false);
                setProcessingOrder(false);
              }}
              primaryButtonClick={() => {
                setShowContinueToNextWarningModal(false);
                setNoValidLines(false);
                setHasMissingSerialNumbers(false);
                handleContinueToNext(false);
              }}
              noValidLines={noValidLines}
              hasMissingSerialNumbers={hasMissingSerialNumbers}
            />
          )}

        <OrderFileUploadModal
          openModal={showFileUploadModal}
          importLinesButtonCallback={(files) => {
            setShowFileUploadModal(false);
            parseFiles(files);
          }}
          closeModalCallback={() => {
            setShowFileUploadModal(false);
          }}
        />

        {isOrderLinesImporting && <LoadingIndicator overlayLoader />}

        {ENABLE_MAX_ORDER_LINES_UI_VALIDATION &&
          enteredRowCount() > MAX_ORDER_LINES && (
            <Alert
              id="maxLines-alert"
              data-testid="maxLines-alert"
              variant="error"
              sx={{
                marginBottom: "1em",
              }}
            >
              {t("orders:orderLines.error.maxLinesExceeded")}
            </Alert>
          )}

        <EntryLinesHeader
          selectedRow={selectedRow}
          enteredRowCount={enteredRowCount()}
        />

        {numberOfErrors > 0 && (
          <Alert
            id="hasIssues-alert"
            data-testid="hasIssues-alert"
            variant="warning"
            sx={{
              marginBottom: "1em",
            }}
          >
            {t("orders:orderLines.orderGrid.lines.hasIssues", {
              countIssues: numberOfErrors,
            })}
          </Alert>
        )}

        <DataGridPro
          columnBufferPx={displayColumns.length + 1}
          rowBufferPx={100}
          data-testid="order-data-grid-table"
          editMode="row"
          apiRef={apiRef}
          columns={displayColumns}
          rows={visibleRows}
          processRowUpdate={processRowUpdate}
          getRowHeight={() => "auto"}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnReorder
          hideFooter
          sx={dataGridSx}
          slots={{
            toolbar: () => (
              <EntryLinesToolbar
                {...{
                  viewIssuesOnly,
                  changeViewIssuesOnly,
                  selectedRow,
                  deleteButtonCallback,
                  resetButtonCallback,
                  isResetButtonDisabled,
                  isViewErrorsEnabled: numberOfErrors > 0,
                  onViewErrorsChange: (checked: boolean) =>
                    setShowRowsWithIssuesOnly(checked),
                  importLinesButtonCallback,
                  isMaxOrderLinesReached: enteredRowCount() >= MAX_ORDER_LINES,
                  addRows,
                }}
              />
            ),
          }}
          slotProps={{}}
          onRowSelectionModelChange={(ids) => onRowsSelectionHandler(ids)}
          rowSelectionModel={selectedRow.map((r: GridRowModel) => r.id)}
          pagination
          onCellClick={handleCellClick}
          onCellEditStop={async (params) => {
            const { id } = params;
            const rowData = visibleRows.find((row) => row.id === id);
            if (rowData) {
              try {
                await processRowUpdate(rowData);
              } catch (error) {
                console.error("Error processing row update:", error);
              }
            }
          }}
          onStateChange={(state) => {
            const parentRowCount = gridFilteredTopLevelRowCountSelector(
              state,
              apiRef.current.instanceId
            );
            setUniqueRows((prev) =>
              prev === parentRowCount ? prev : parentRowCount
            );
          }}
        />

        <StickyFooter
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <OrderLinesPagination
            page={page + 1}
            pageSize={pageSize}
            rowLength={uniqueRows}
            setPage={setPage}
            setPageSize={setPageSize}
            api={apiRef.current}
          />
          <div>
            {cancelOrderButton}
            {continueToNextButton}
          </div>
        </StickyFooter>
      </>
    );
  }
);

export default EntryLines;
